import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   *
   * Round 21 (Tug-E-Nuff) removed the dedicated root-panel close button, so
   * the header's own hamburger<->close `<summary>` (see the `on:click="/toggle"`
   * above in header-drawer.liquid) is now the only control for BOTH opening
   * and closing the root drawer. Clicking a `<summary>` also runs the
   * browser's own default action — toggling the parent `<details>`'s `open`
   * attribute — alongside this handler. That was harmless before, because
   * the summary only ever drove the closed -> open transition (closing
   * always went through the dedicated button's explicit `/close`, which
   * never touches the native toggle). Now it drives both directions, so the
   * native toggle and this method's own state changes could each flip
   * `open` independently on the same click, drifting the attribute out of
   * sync with the visible `.menu-open` class after repeated closes and
   * needing an extra click to catch up. preventDefault() makes this method
   * the single source of truth for the `open` attribute for THIS button —
   * it sets the attribute itself before calling open(), rather than having
   * open() do it. (An earlier version of this fix had open() itself always
   * set the attribute, on the assumption that would be harmless/redundant
   * for submenu triggers too — it wasn't: submenu "open/submenu" triggers
   * don't call toggle() and so still rely on the native default action,
   * unprevented, to set `open`. With open() ALSO setting it explicitly
   * first, the native default then ran straight after and flipped it back
   * off, breaking every submenu trigger — Shop/Play & Learn/About stopped
   * opening at all. Scoping the explicit set to toggle() only fixes the
   * root button without touching that still-native-dependent path.)
   *
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    if (this.isOpen) return this.close();

    // Chris Nelis-Rouse — 4 Sep 2026: scroll to the top BEFORE opening the drawer.
    //
    // This is the same Round 27/28 scroll-to-top fix that used to live in open() — see the
    // long comment there for why it exists at all. The behaviour is unchanged; only the
    // ordering moved, because the old order was a textbook forced synchronous layout:
    // `setAttribute('open')` just below reveals the entire drawer subtree (30 links plus
    // submenus), and a scroll needs up-to-date layout, so scrollTo() then forced the browser
    // to lay that whole newly-revealed subtree out synchronously, inside the click handler.
    // That is why the "Menu" interaction reported 596ms of *processing* time when every other
    // slow interaction on this site is presentation delay.
    //
    // Measured live on the storefront, five open/close cycles of each order:
    //   setAttribute then scrollTo (old):  34.4 / 26.3 / 30.0 / 18.9 / 20.9ms  (median 26.3)
    //   scrollTo then setAttribute (new):   0.0 /  0.1 /  0.0 /  0.2 /  0.2ms  (median 0.1)
    // Scrolling first flushes the layout of the still-closed page, which is already laid out
    // and therefore free; the drawer's first layout then happens in the frame's normal render
    // step instead of being forced twice.
    //
    // Safe to move here because open() only reaches its scroll branch when called with no
    // `target`, and the only path that does that is this method — every other call site is
    // `on:click="header-drawer/open/submenu"`, which passes a target and skipped the scroll
    // anyway. (The `on:toggle="/toggle"` in header-drawer.liquid resolves to
    // drawer-localization-component, not this component.)
    scrollTo({ top: 0, left: 0, behavior: 'instant' });

    this.refs.details.setAttribute('open', '');
    return this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (!target) {
      // Round 27 (Tug-E-Nuff) — Chris: opening the root drawer after
      // scrolling down the page could leave the header hidden behind it.
      // assets/header.js tracks scroll direction and hides the sticky
      // header on scroll-down, only restoring it on scroll-up or once back
      // at the very top (see its #updateScrollState) — that state doesn't
      // know the drawer just opened. Scrolling to the top here (root drawer
      // only; submenu opens skip this since the root is already open and
      // already scrolled by then) puts header.js's own logic into the
      // "at top" branch, which resets it to its normal visible state.
      // Chris suggested exactly this fix. Using the theme's own
      // scroll-container helper (assets/scroll-container.js) rather than
      // raw window.scrollTo — on the desktop "squeeze" layout (>=990px)
      // the real scroll container is `.page-wrapper`, not the window, and
      // this stays correct if the drawer is ever opened from there too.
      //
      // Round 28 follow-up — Chris found this visibly scrolled/animated
      // rather than jumping straight to the top, and asked either for a
      // cover-up transition or a much faster/instant jump. `behavior:
      // 'auto'` sounds like it should mean "no animation", but per the
      // scroll spec 'auto' actually defers to the CSS `scroll-behavior`
      // property of the scrolling box — and assets/base.css sets
      // `scroll-behavior: smooth` on both `html` (mobile) and
      // `.page-wrapper` (desktop squeeze layout), the exact two containers
      // getScrollContainer() can return. So 'auto' was quietly resolving to
      // a smooth animated scroll here. `instant` bypasses CSS
      // scroll-behavior entirely and jumps immediately — this is the same
      // fix (down to the comment) the theme's own scroll-container.js
      // already uses for its own scroll-restore-on-navigation logic, see
      // restoreSavedScrollTop()'s "Use scrollTo with 'instant' to override
      // CSS scroll-behavior: smooth" a few lines up in that file.
      //
      // Round 34 — Chris Nelis-Rouse, 4 Sep 2026: the scroll itself now runs in toggle(),
      // BEFORE the `open` attribute is set — see that method for the measurements. Running it
      // here meant scrolling after the drawer subtree had been revealed, which forced a
      // synchronous layout of the whole drawer inside the click handler (median 26.3ms
      // unthrottled, and the 596ms of processing time in the "Menu" interaction report).
      // Nothing is needed here now; the branch is kept so the reasoning above stays attached
      // to the root-open path it documents.
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}